import { useGetBeamerChannel, useGetCurrentUser } from 'gcs-common/clients/api/entities/current-user/current-user-api';
import { useGetBeamerChatEnabled } from 'gcs-common/clients/api/entities/current-user/current-user-api-selectors';
import LoadingIndicator from 'gcs-common/components/LoadingIndicator/LoadingIndicator';
import { CHAT_ROUTES } from 'gcs-common/constants/routes';
import { loadScript } from 'gcs-common/helper/loadScript';
import { getAppTheme } from 'gcs-common/slices/theme/themeSelectors';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router';

declare global {
  interface Window {
    Beamer?: {
      init: (config?: Record<string, unknown>) => void;
      destroy?: () => void;
    };
    beamer_config?: Record<string, unknown>;
  }
}

const BEAMER_SCRIPT_URL = import.meta.env.VITE_BEAMER_SCRIPT_URL;
const BEAMER_PROJECT_ID = import.meta.env.VITE_BEAMER_PROJECT_ID;

const containerId = 'beamer-feed-container';

const BeamerFeed = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const theme = useSelector(getAppTheme);
  const isBeamerChatEnabled = useGetBeamerChatEnabled();
  const { data: me } = useGetCurrentUser();
  const { data: beamerData } = useGetBeamerChannel();
  const filter = beamerData?.filter;

  useEffect(() => {
    if (!filter) {
      return;
    }
    // Moved inside the useEffect instead of under it to save
    // the unnecessary beamer call
    if (!isBeamerChatEnabled) {
      navigate(CHAT_ROUTES.CHAT);
      return;
    }
    if (!BEAMER_PROJECT_ID || !BEAMER_SCRIPT_URL || !me) {
      return undefined;
    }

    let cancelled = false;

    const initBeamer = async () => {
      if (cancelled) {
        return;
      }

      window.beamer_config = {
        product_id: BEAMER_PROJECT_ID,
        language: 'de',
        selector: containerId,
        lazy: true,
        embed: true,
        user_id: me.id,
        theme: `theme-${theme}`,
        filter,
        callback: () => {
          setIsLoading(false);
          const iframe = document.getElementById('beamerNews') as HTMLIFrameElement | null;
          const chatContainer = iframe?.contentWindow?.document?.getElementById('newsContainerDiv');
          if (chatContainer) {
            chatContainer.scrollTop = chatContainer.scrollHeight;
          }
        },
      };

      if (!window.Beamer) {
        try {
          await loadScript(BEAMER_SCRIPT_URL);
        } catch {
          return;
        }
      }

      if (!cancelled && window.Beamer) {
        window.Beamer.init();
      }
    };

    void initBeamer();

    const teardown = () => {
      cancelled = true;
      window.Beamer?.destroy?.();
      window.Beamer = undefined;
      window.beamer_config = undefined;
    };

    return teardown;
  }, [me, theme, BEAMER_PROJECT_ID, BEAMER_SCRIPT_URL, isBeamerChatEnabled, filter]);

  return (
    <div className="h-full w-full overflow-auto" id={containerId}>
      {isLoading && (
        <div className="flex h-full items-center justify-center">
          <LoadingIndicator />
        </div>
      )}
    </div>
  );
};

export default BeamerFeed;
